import React, { useState, useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../config/api';
import {
  buildAgentTransactionContext,
  persistAgentTransactionContext,
  clearAgentTransactionContext,
} from '../utils/agentTransactionContext';
import CoverPurchase from './CoverPurchase';
import './UnderwritingIssueCover.css';

const DEBOUNCE_MS = 400;

function normalizeIssueAgent(agent) {
  if (!agent) return agent;
  const tq = String(agent.tq_code || agent.tqCode || agent.short_description || '').trim();
  const kra = String(agent.kra_pin || agent.kraPin || '').trim();
  return { ...agent, tq_code: tq, kra_pin: kra };
}

/**
 * Underwriter: issue cover on behalf of an agent, or as direct business (no agent / no commission).
 * When comprehensiveOnly: Comprehensive-only issue flow (same UIs, COMP product only).
 */
const UnderwritingIssueCover = ({ user, onShowNotification, startWithExtension = false, comprehensiveOnly = false }) => {
  const API_BASE_URL = getApiBaseUrl();
  const [businessMode, setBusinessMode] = useState(null); // 'agent' | 'direct' | null
  const [agencySearch, setAgencySearch] = useState('');
  const [agencySearchApplied, setAgencySearchApplied] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [purchaseMode, setPurchaseMode] = useState(null); // 'agent' | 'direct'
  const [purchaseAgent, setPurchaseAgent] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setAgencySearchApplied(agencySearch.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [agencySearch]);

  useEffect(() => {
    if (!agencySearchApplied || businessMode !== 'agent') {
      setAgents([]);
      setAgentsError('');
      return;
    }
    let cancelled = false;
    setAgentsLoading(true);
    setAgentsError('');
    const token = localStorage.getItem('adminToken');
    fetch(`${API_BASE_URL}/api/admin/underwriting/agents?search=${encodeURIComponent(agencySearchApplied)}&limit=30`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data.success && Array.isArray(data.agents)) {
          setAgents(data.agents.map(normalizeIssueAgent));
          setAgentsError('');
          return;
        }
        setAgents([]);
        setAgentsError(data.error || `Could not search agents (${res.status})`);
      })
      .catch(() => {
        if (!cancelled) {
          setAgents([]);
          setAgentsError('Could not search agents. Please try again.');
        }
      })
      .finally(() => { if (!cancelled) setAgentsLoading(false); });
    return () => { cancelled = true; };
  }, [agencySearchApplied, API_BASE_URL, businessMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAgencySearchApplied('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectAgent = (agent) => {
    setSelectedAgent(normalizeIssueAgent(agent));
    setAgencySearch('');
    setAgencySearchApplied('');
  };

  const clearSelection = () => {
    setSelectedAgent(null);
  };

  const resetMode = () => {
    setBusinessMode(null);
    setSelectedAgent(null);
    setAgencySearch('');
    setAgencySearchApplied('');
  };

  const startPurchaseForAgent = () => {
    if (!selectedAgent) return;
    const context = buildAgentTransactionContext({
      agentId: selectedAgent.id,
      agency: {
        shortDescription: selectedAgent.tq_code || '',
        taxPin: selectedAgent.kra_pin || ''
      },
      issuedByAdminId: user?.id
    });
    if (context) persistAgentTransactionContext(context);
    setPurchaseAgent(selectedAgent);
    setPurchaseMode('agent');
  };

  const startDirectPurchase = () => {
    const context = buildAgentTransactionContext({
      issuedByAdminId: user?.id,
      isDirectBusiness: true
    });
    if (context) persistAgentTransactionContext(context);
    setPurchaseAgent(null);
    setPurchaseMode('direct');
  };

  const handlePurchaseClose = () => {
    clearAgentTransactionContext();
    setPurchaseMode(null);
    setPurchaseAgent(null);
  };

  const handlePurchaseComplete = () => {
    clearAgentTransactionContext();
    setPurchaseMode(null);
    setPurchaseAgent(null);
    if (onShowNotification) {
      onShowNotification('Cover issued successfully. It will appear under Covers Issued.', 'success');
    }
  };

  if (purchaseMode === 'direct') {
    return (
      <div className="underwriting-issue-cover underwriting-purchase-flow">
        <CoverPurchase
          issuedByAdminId={user?.id}
          user={user}
          isDirectBusiness
          comprehensiveOnly={comprehensiveOnly}
          initialEntryStep="product-selection"
          onBehalfOfLabel="Direct business"
          onClose={handlePurchaseClose}
          onPurchaseComplete={handlePurchaseComplete}
        />
      </div>
    );
  }

  if (purchaseMode === 'agent' && purchaseAgent) {
    return (
      <div className="underwriting-issue-cover underwriting-purchase-flow">
        <CoverPurchase
          agentId={purchaseAgent.id}
          agency={{
            shortDescription: purchaseAgent.tq_code || '',
            taxPin: purchaseAgent.kra_pin || ''
          }}
          issuedByAdminId={user?.id}
          user={user}
          comprehensiveOnly={comprehensiveOnly}
          initialEntryStep={startWithExtension && !comprehensiveOnly ? 'extension-finder' : 'product-selection'}
          onBehalfOfLabel={purchaseAgent.agency_name || purchaseAgent.username}
          onClose={handlePurchaseClose}
          onPurchaseComplete={handlePurchaseComplete}
        />
      </div>
    );
  }

  return (
    <div className="underwriting-issue-cover">
      <div className="underwriting-section card">
        <h3>{comprehensiveOnly ? 'Comprehensive cover — how are you issuing?' : 'How are you issuing this cover?'}</h3>
        <p className="underwriting-hint">
          {comprehensiveOnly
            ? 'Pilot Comprehensive only (CGICHEHA). Same flow as agents — choose agent or direct business, then continue with the Comprehensive product card.'
            : 'Choose agent business to link the cover to an intermediary, or direct business for Monarch direct sales (no commission).'}
        </p>
        <div className="business-mode-options">
          <button
            type="button"
            className={`business-mode-card${businessMode === 'agent' ? ' selected' : ''}`}
            onClick={() => setBusinessMode('agent')}
          >
            <span className="business-mode-number">1</span>
            <span className="business-mode-title">Agent business</span>
            <span className="business-mode-desc">Search and select an active agent by agency name</span>
          </button>
          <button
            type="button"
            className={`business-mode-card${businessMode === 'direct' ? ' selected' : ''}`}
            onClick={() => {
              setBusinessMode('direct');
              setSelectedAgent(null);
            }}
          >
            <span className="business-mode-number">2</span>
            <span className="business-mode-title">Direct business</span>
            <span className="business-mode-desc">
              {comprehensiveOnly
                ? 'No agent — Comprehensive direct sale (no commission)'
                : 'No agent — TPO or TOR cover with zero commission to Core'}
            </span>
          </button>
        </div>
        {businessMode && businessMode !== 'direct' && (
          <button type="button" className="btn-clear mode-back" onClick={resetMode}>Change option</button>
        )}
      </div>

      {businessMode === 'direct' && (
        <div className="underwriting-section card direct-business-card">
          <h3>{comprehensiveOnly ? 'Direct business — Comprehensive' : 'Direct business cover'}</h3>
          <p className="underwriting-hint">
            {comprehensiveOnly
              ? <>The cover will be recorded as <strong>Direct</strong>. Comprehensive: 1 month · 30% payable · production DMVIC · Core after paid cert.</>
              : <>The cover will be recorded as <strong>Direct</strong> in reports. No agent commission is calculated and Core receives <code>commissionAllowed: N</code> with a blank agency.</>}
          </p>
          <button type="button" className="btn-primary btn-start-purchase" onClick={startDirectPurchase}>
            {comprehensiveOnly ? 'Start Comprehensive purchase (direct)' : 'Start TPO / TOR purchase (direct)'}
          </button>
          <button type="button" className="btn-clear mode-back" onClick={resetMode}>Back</button>
        </div>
      )}

      {businessMode === 'agent' && (
        <div className="underwriting-section card">
          <h3>1. Search and select an active agent (by Agency Name)</h3>
          <p className="underwriting-hint">Only active agents are listed. Select the agent on whose behalf you are issuing the cover.</p>
          <div className="form-group" ref={dropdownRef}>
            <label>Agency name</label>
            <input
              type="text"
              value={agencySearch}
              onChange={(e) => setAgencySearch(e.target.value)}
              placeholder="Type agency name to search..."
              className="input-search"
            />
            {agentsLoading && <span className="loading-inline">Searching…</span>}
            {agencySearchApplied && !agentsLoading && (
              <ul className="agent-dropdown">
                {agentsError ? (
                  <li className="no-results search-error">{agentsError}</li>
                ) : agents.length === 0 ? (
                  <li className="no-results">No active agents found</li>
                ) : (
                  agents.map((a) => (
                    <li
                      key={a.id}
                      className={selectedAgent?.id === a.id ? 'selected' : ''}
                      onClick={() => selectAgent(a)}
                    >
                      <strong>{a.agency_name || a.username}</strong>
                      {a.ira_number && <span className="ira"> ({a.ira_number})</span>}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {selectedAgent && (
            <div className="selected-agent-card">
              <div className="selected-agent-header">
                <span className="label">Selected agent</span>
                <button type="button" className="btn-clear" onClick={clearSelection}>Clear</button>
              </div>
              <p className="agency-name">{selectedAgent.agency_name || selectedAgent.username}</p>
              {(() => {
                const hasTq = !!(selectedAgent.tq_code && String(selectedAgent.tq_code).trim());
                const hasKra = !!(selectedAgent.kra_pin && String(selectedAgent.kra_pin).trim());
                const missingParts = [];
                if (!hasTq) missingParts.push('TQ Short Description');
                if (!hasKra) missingParts.push('KRA Pin');
                const disabled = !hasTq || !hasKra;
                return (
                  <>
                    <div className="tq-kra-row">
                      <div className="field">
                        <span className="field-label">TQ Short Description</span>
                        <span className="field-value">{selectedAgent.tq_code || '—'}</span>
                      </div>
                      <div className="field">
                        <span className="field-label">KRA Pin</span>
                        <span className="field-value">{selectedAgent.kra_pin || '—'}</span>
                      </div>
                    </div>
                    <p className="hint">
                      Agent ID, TQ and KRA Pin are preloaded for the whole transaction and will be used to link the business and send to Core.
                      {disabled && missingParts.length > 0 && (
                        <>
                          {' '}<strong>Missing:</strong> {missingParts.join(' and ')}. Please update the agent record before issuing a cover.
                        </>
                      )}
                    </p>
                    <button
                      type="button"
                      className="btn-primary btn-start-purchase"
                      onClick={startPurchaseForAgent}
                      disabled={disabled}
                    >
                      {startWithExtension ? 'Start buy/extend for this agent' : 'Start cover purchase for this agent'}
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnderwritingIssueCover;
